// # Revenue Timeline Component
import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './bnct_revenue_timeline.css';

const RevenueTimeline = ({ revenueData, member }) => {
  // # Determine if this is a project budget account
  const isProjectBudget = member && member.id === 'project-budget';

  // # Process revenueOverTime to create chart data
  const timelineData = useMemo(() => {
    if (!revenueData || !revenueData.revenueOverTime) return [];
    
    const result = [];
    
    // # Sort months chronologically
    const sortedMonths = Object.keys(revenueData.revenueOverTime).sort();
    
    // # Process each month's data
    sortedMonths.forEach(month => {
      const monthData = revenueData.revenueOverTime[month];
      
      // # For project budget account
      if (isProjectBudget) {
        result.push({
          month,
          budget: monthData.budget || 0,
          expenses: monthData.expenses || 0,
          net: monthData.net || 0
        });
      } 
      // # For team member accounts
      else {
        result.push({
          month,
          revenue: monthData.revenue || 0,
          salaries: monthData.salaries || 0,
          net: monthData.net || 0
        });
      }
    });
    
    return result;
  }, [revenueData, isProjectBudget]);
  
  // # Calculate total net revenue from timeline data for percentage calculations
  const totalNetRevenueFromTimeline = useMemo(() => {
    return timelineData.reduce((sum, data) => sum + (data.revenue || 0), 0);
  }, [timelineData]);
  
  // # Calculate growth statistics
  const growthStats = useMemo(() => {
    return calculateGrowthStats(timelineData, isProjectBudget);
  }, [timelineData, isProjectBudget]);
  
  // # Use stored values directly from the updated data structure
  // # Total revenue is now the gross amount (before budget)
  const totalRevenue = revenueData?.totalRevenue || 0;
  
  // # Check if data is available
  if (!revenueData) return <div className="bnct_loading">Loading timeline data...</div>;
  
  return (
    <div className="bnct_timeline-container">
      <h3 className="bnct_section-title">{isProjectBudget ? 'Budget Timeline' : 'Revenue Timeline'}</h3>
      
      {/* # Timeline Chart */}
      <div className="bnct_timeline-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={formatMonth} />
            <YAxis />
            <Tooltip formatter={(value) => `E£ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <Legend />
            
            {/* # Different areas based on account type */}
            {isProjectBudget ? (
              <>
                <Area 
                  type="monotone" 
                  dataKey="budget" 
                  stackId="1" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  name="Budget Allocation" 
                />
                <Area 
                  type="monotone" 
                  dataKey="expenses" 
                  stackId="2" 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  name="Expenses" 
                />
                <Area 
                  type="monotone" 
                  dataKey="net" 
                  stackId="3" 
                  stroke="#ffc658" 
                  fill="#ffc658" 
                  name="Net Budget" 
                />
              </>
            ) : (
              <>
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stackId="1" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  name="Net Revenue" 
                />
                <Area 
                  type="monotone" 
                  dataKey="salaries" 
                  stackId="2" 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  name="Salaries" 
                />
                <Area 
                  type="monotone" 
                  dataKey="net" 
                  stackId="3" 
                  stroke="#ffc658" 
                  fill="#ffc658" 
                  name="Available Cash" 
                />
              </>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* # Growth Statistics */}
      <div className="bnct_growth-stats">
        <h3>Growth Statistics</h3>
        <div className="bnct_stats-grid">
          <div className="bnct_stat-card">
            <h4>Monthly Average</h4>
            <p>E£ {growthStats.monthlyAverage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          
          <div className="bnct_stat-card">
            <h4>Growth Rate</h4>
            <p className={growthStats.growthRate >= 0 ? "positive" : "negative"}>
              {growthStats.growthRate >= 0 ? "+" : ""}{growthStats.growthRate.toFixed(2)}%
            </p>
          </div>
          
          <div className="bnct_stat-card">
            <h4>Highest Month</h4>
            <p>{formatMonth(growthStats.highestMonth.month)}: E£ {growthStats.highestMonth.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          
          <div className="bnct_stat-card">
            <h4>Lowest Month</h4>
            <p>{formatMonth(growthStats.lowestMonth.month)}: E£ {growthStats.lowestMonth.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      </div>
      
      {/* # Monthly Breakdown Table */}
      <div className="bnct_timeline-table-container">
        <h3>Monthly Breakdown</h3>
        <div className="bnct_timeline-table-wrapper">
          <table className="bnct_timeline-table">
            <thead>
              <tr>
                <th>Month</th>
                {isProjectBudget ? (
                  <>
                    <th>Budget Allocation</th>
                    <th>Expenses</th>
                    <th>Net Budget</th>
                  </>
                ) : (
                  <>
                    <th>Net Revenue</th>
                    <th>Salaries</th>
                    <th>Available Cash</th>
                    <th>% of Net Revenue</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {timelineData.map(data => (
                <tr key={data.month}>
                  <td>{formatMonth(data.month)}</td>
                  {isProjectBudget ? (
                    <>
                      <td>E£ {(data.budget || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>E£ {(data.expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>E£ {(data.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </>
                  ) : (
                    <>
                      <td>E£ {(data.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>E£ {(data.salaries || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>E£ {(data.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{(((data.revenue || 0) / (totalNetRevenueFromTimeline || 1)) * 100).toFixed(2)}%</td>
                    </>
                  )}
                </tr>
              ))}
              <tr className="bnct_timeline-total">
                <td>Total</td>
                {isProjectBudget ? (
                  <>
                    <td>E£ {revenueData.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>E£ {(revenueData.expenseShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>E£ {(revenueData.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </>
                ) : (
                  <>
                    <td>E£ {totalNetRevenueFromTimeline.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>E£ {(revenueData.salariesDeducted || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>E£ {(revenueData.availableCash || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>100%</td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// # Helper function to format month for display
const formatMonth = (month) => {
  if (!month) return 'N/A';
  const [year, monthNum] = month.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
};

// # Helper function to calculate growth statistics
const calculateGrowthStats = (timelineData, isProjectBudget) => {
  if (!timelineData || timelineData.length === 0) {
    return {
      monthlyAverage: 0,
      growthRate: 0,
      highestMonth: { month: 'N/A', value: 0 },
      lowestMonth: { month: 'N/A', value: 0 }
    };
  }
  
  // # Calculate monthly average
  let total = 0;
  let highestMonth = { month: timelineData[0].month, value: 0 };
  let lowestMonth = { month: timelineData[0].month, value: Number.MAX_SAFE_INTEGER };
  
  timelineData.forEach(data => {
    // # Get the appropriate value based on data structure and account type
    const value = isProjectBudget ? data.budget : data.revenue;
    total += value || 0;
    
    // # Track highest and lowest months
    if ((value || 0) > highestMonth.value) {
      highestMonth = { month: data.month, value: value || 0 };
    }
    
    if ((value || 0) < lowestMonth.value && (value || 0) > 0) {
      lowestMonth = { month: data.month, value: value || 0 };
    }
  });
  
  const monthlyAverage = total / timelineData.length;
  
  // # Calculate growth rate (if we have at least 2 months of data)
  let growthRate = 0;
  if (timelineData.length >= 2) {
    const firstMonthValue = isProjectBudget ? 
      timelineData[0].budget || 0 : 
      timelineData[0].revenue || 0;
    
    const lastMonthValue = isProjectBudget ? 
      timelineData[timelineData.length - 1].budget || 0 : 
      timelineData[timelineData.length - 1].revenue || 0;
    
    if (firstMonthValue > 0) {
      growthRate = ((lastMonthValue - firstMonthValue) / firstMonthValue) * 100;
    }
  }
  
  // # If lowest month is still MAX_SAFE_INTEGER, set it to 0
  if (lowestMonth.value === Number.MAX_SAFE_INTEGER) {
    lowestMonth = { month: 'N/A', value: 0 };
  }
  
  return {
    monthlyAverage,
    growthRate,
    highestMonth,
    lowestMonth
  };
};

export default RevenueTimeline; 